import { useRef, type ReactNode } from 'react';
import { EMPTY_OBJECT } from '../../../utils';
import { ButtonVariants, ToggleButton, type ButtonProps } from '../../Button';
import { Box, type BoxProps } from '../../Core';
import { Tooltip } from '../../Tooltip';
import { FormControlSizes } from '../types';
import { SelectionIndicator, ToggleGroupRoot } from './styles';
import { useToggleGroupIndicator } from './useToggleGroupIndicator';
import { useToggleGroupTooltips } from './useToggleGroupTooltips';

export type ToggleGroupOption<ValueType extends string> = {
  value: ValueType;
  displayName: ReactNode;
  id?: string;
  tooltip?: React.ReactNode;
  buttonVariant?: ButtonVariants;
  startIcon?: ButtonProps['startIcon'];
  endIcon?: ButtonProps['endIcon'];
  disabled?: boolean;
  testId?: string;
};

export type ToggleGroupProps<TValue extends string> = {
  options: Array<ToggleGroupOption<TValue>>;
  onChange: (value: TValue) => void;
  /** undefined is ok for unselected state */
  value: TValue | undefined;
  /** When value is empty, show this as selected (display only) */
  defaultValue?: TValue;
  size?: FormControlSizes;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  /** Selected-indicator animation. Defaults to slide. */
  selectionAnimation?: 'none' | 'slide';
  boxProps?: BoxProps;
  'data-testid'?: string;
  /**
   * Fallback variant applied to the selected button when an option has no explicit buttonVariant.
   * Unselected buttons without buttonVariant still follow the default muted (data-toggle-default='true') styling.
   */
  defaultSelectedVariant?: ButtonVariants;
  /**
   * Preserve legacy grouped-button selection attributes used by existing POs/Cypress tests.
   * Set to false when strict radio semantics are required.
   */
  legacyAriaSelected?: boolean;
  /**
   * When horizontal, give every option the same width (flex share) instead of sizing to label length.
   * Pair with a definite group width (`boxProps.w`) so items have space to share.
   */
  equalWidth?: boolean;
};

/**
 * ToggleGroup is a grouped single-select control supporting
 * variants, orientation, icons, and tooltips per option.
 */
export const ToggleGroup = <TValue extends string>({
  options,
  onChange,
  value,
  defaultValue,
  disabled = false,
  size = FormControlSizes.Default,
  orientation = 'horizontal',
  selectionAnimation = 'slide',
  boxProps = EMPTY_OBJECT,
  'data-testid': dataTestId,
  defaultSelectedVariant,
  legacyAriaSelected = true,
  equalWidth = false,
}: ToggleGroupProps<TValue>) => {
  const effectiveValue = value == null || value === '' ? defaultValue : value;
  const useSlideAnimation = selectionAnimation === 'slide';
  const selectedOption = options.find(option => option.value === effectiveValue);
  const selectedVariant =
    selectedOption?.buttonVariant ??
    (selectedOption != null && defaultSelectedVariant != null ? defaultSelectedVariant : ButtonVariants.Default);
  const isSelectedDisabled = disabled || selectedOption?.disabled === true;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const indicator = useToggleGroupIndicator({
    useSlideAnimation,
    value: effectiveValue,
    options,
    orientation,
    size,
    rootRef,
    itemRefs,
  });
  const showIndicator = useSlideAnimation && indicator.visible;
  const shareHorizontalWidth = orientation === 'horizontal' && equalWidth;
  const equalWidthLayout = shareHorizontalWidth
    ? {
        display: 'grid' as const,
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        minWidth: 0,
        w: '100%' as const,
        flexShrink: 1,
      }
    : EMPTY_OBJECT;
  const { tooltipDelay, handleGroupMouseLeave, handleOpen, handleOptionMouseEnter, getContentStyle } =
    useToggleGroupTooltips<TValue>({ onGroupMouseLeave: boxProps.onMouseLeave });

  return (
    <ToggleGroupRoot
      ref={rootRef}
      orientation={orientation}
      size={size}
      type="switch"
      gap="spacing2"
      p="spacing2"
      borderRadius="borderRadiusMedium"
      background="backgroundToggle"
      aria-disabled={disabled ? 'true' : undefined}
      {...(boxProps.flex == null && boxProps.flexShrink == null && !shareHorizontalWidth
        ? { flexShrink: 0 }
        : EMPTY_OBJECT)}
      {...equalWidthLayout}
      {...boxProps}
      onMouseLeave={handleGroupMouseLeave}
      display={shareHorizontalWidth ? 'grid' : 'flex'}
      data-testid={dataTestId}
      role="radiogroup"
    >
      {showIndicator && (
        <SelectionIndicator
          $variant={selectedVariant}
          $x={indicator.x}
          $y={indicator.y}
          $width={indicator.width}
          $height={indicator.height}
          data-testid={dataTestId ? `${dataTestId}:indicator` : undefined}
          position="absolute"
          left="0"
          top="0"
          borderRadius="borderRadiusButtonDefault"
          // The real button underneath is forced ghost+Default under slide animation (see below), so
          // it carries no color to dim -- the indicator has to apply the disabled opacity itself.
          opacity={isSelectedDisabled ? 0.5 : 1}
          transition="transform 200ms cubic-bezier(0.4, 0, 0.2, 1), width 200ms ease, height 200ms ease, opacity 150ms ease, background-color 200ms ease"
          pointerEvents="none"
          zIndex={0}
        />
      )}
      {options.map(option => {
        const selected = effectiveValue === option.value;
        const isDisabled = disabled || option.disabled === true;
        return (
          <Tooltip
            tooltipTestID={dataTestId ? `${dataTestId}:${option.value}:tooltip` : undefined}
            tooltip={option.tooltip}
            delay={tooltipDelay}
            hideDelay={tooltipDelay}
            onOpen={handleOpen}
            targetStyle={
              orientation === 'horizontal' ? { flex: '1', minWidth: shareHorizontalWidth ? 0 : undefined } : undefined
            }
            contentStyle={getContentStyle(option.value)}
            key={`toggleGroup-${option.value}`}
          >
            <Box
              ref={node => {
                itemRefs.current[String(option.value)] = node;
              }}
              onMouseEnter={() => handleOptionMouseEnter(option.value)}
              position="relative"
              flex={orientation === 'horizontal' ? 1 : undefined}
              minWidth={orientation === 'horizontal' ? (shareHorizontalWidth ? 0 : 'max-content') : undefined}
              overflow={shareHorizontalWidth ? 'hidden' : undefined}
              display={orientation === 'vertical' ? 'inline-flex' : undefined}
              zIndex={1}
              h="100%"
            >
              <ToggleButton
                key={`toggleGroup-button-${option.value}`}
                selected={selected}
                aria-checked={selected}
                id={option.id}
                disabled={isDisabled}
                unselectedVariant={ButtonVariants.Default}
                selectedVariant={
                  useSlideAnimation
                    ? ButtonVariants.Default
                    : (option.buttonVariant ??
                      (selected ? defaultSelectedVariant : undefined) ??
                      ButtonVariants.Default)
                }
                ghost={useSlideAnimation}
                size={size}
                width={orientation === 'horizontal' ? '100%' : undefined}
                onClick={() => !isDisabled && onChange(option.value)}
                data-testid={option.testId || (dataTestId ? `${dataTestId}:${option.value}` : undefined)}
                data-toggle-default={
                  option.buttonVariant == null && !(defaultSelectedVariant != null && selected) ? 'true' : 'false'
                }
                data-toggle-animated={useSlideAnimation ? 'true' : undefined}
                data-toggle-transparent={useSlideAnimation || selected ? undefined : 'true'}
                data-toggle-variant={
                  option.buttonVariant ??
                  (selected && defaultSelectedVariant != null ? defaultSelectedVariant : ButtonVariants.Default)
                }
                startIcon={option.startIcon}
                endIcon={option.endIcon}
                grouped
                legacyAriaSelected={legacyAriaSelected}
                role="radio"
              >
                {option.displayName}
              </ToggleButton>
            </Box>
          </Tooltip>
        );
      })}
    </ToggleGroupRoot>
  );
};
